import React, {
  memo,
  type ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDetectScroll } from '@saviynt/common';
import {
  Box,
  Button,
  FooterActionBar,
  Icon,
  InlineMessage,
  InputField,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';
import { format } from 'date-fns';

import { CF_NEU_BLD, MIN_COMMENT_LENGTH } from '../../../constants';
import { useInvertedLocalize } from '../../../hooks';

import EndDatePicker from './EndDatePicker';
import TimeRangePicker from './TimeRangePicker';

import './SodConsultActionModal.css';

interface SodConsultActionModalProps {
  isOpen: boolean;
  isLoading: boolean;
  headerIcon: ReactElement;
  title: string;
  subtitle: string;
  handleSumbit: (
    comment: string,
    configs?: {
      startDate?: string | null;
      endDate?: string | null;
    }
  ) => void;
  handleModalClose: () => void;
  isDisabled?: boolean;
  isReview?: boolean;
  isEndDate?: boolean;
  currentStartDate?: string | null;
  currentEndDate?: string | null;
  globalDateFormat?: string;
}

/**
 * SodConsultActionModal
 */
function SodConsultActionModal({
  isOpen,
  isDisabled,
  isLoading,
  headerIcon,
  title,
  subtitle,
  handleSumbit,
  handleModalClose,
  isReview,
  isEndDate,
  currentStartDate,
  currentEndDate,
  globalDateFormat,
}: SodConsultActionModalProps) {
  const l = useInvertedLocalize();
  const sectionRef = useRef(null);
  const { isScrolled: sectionRefIsScrolled }: { isScrolled: boolean } =
    useDetectScroll(null, sectionRef);

  const [commentValue, setCommentValue] = useState('');
  const [isDateCritical, setIsDateCritical] = useState(false);
  const [isCommentCritical, setIsCommentCritical] = useState(false);
  const [epoch, setEpoch] = useState<{
    startEpoch: number | null;
    endEpoch: number | null;
  }>({
    startEpoch: 0,
    endEpoch: null,
  });

  // Constants
  const isReviewMode = isReview;
  const isEndDateMode = useMemo(
    () => Boolean(!isReview && isEndDate),
    [isReview, isEndDate]
  );
  const isSubmitButtonEnable = useMemo(
    () => commentValue.trim().length >= MIN_COMMENT_LENGTH,
    [commentValue]
  );

  useEffect(() => {
    if (
      epoch.endEpoch !== null &&
      ((isReviewMode && epoch.startEpoch !== null) || isEndDateMode)
    ) {
      setIsDateCritical(false);
    }
  }, [epoch, isReviewMode, isEndDateMode]);

  const handleModalSubmit = () => {
    // Check for modes and null dates
    if (
      (isReviewMode &&
        !(epoch.startEpoch !== null && epoch.endEpoch !== null)) ||
      (isEndDateMode && !(epoch.endEpoch !== null))
    ) {
      setIsDateCritical(true);

      return;
    }

    const startDate = epoch.startEpoch
      ? format(epoch.startEpoch, 'yyyy-MM-dd HH:mm:ss')
      : null;
    const endDate = epoch.endEpoch
      ? format(epoch.endEpoch, 'yyyy-MM-dd HH:mm:ss')
      : null;

    handleSumbit(commentValue, { startDate, endDate });
  };

  return (
    <ModalIsland
      isOpen={isOpen}
      onClose={handleModalClose}
      headerIcon={headerIcon}
      title={title}
      subtitle={subtitle}
      isCloseButtonVisible={!isDisabled}
      contentBody={
        <Box className='Sod-ConsultAction-bodyWrapper'>
          {isReviewMode && (
            <TimeRangePicker isCritical={isDateCritical} setEpoch={setEpoch} />
          )}
          {isEndDateMode && (
            <EndDatePicker
              isCritical={isDateCritical}
              setIsCritical={setIsDateCritical}
              startDate={currentStartDate}
              currentEndDate={currentEndDate}
              globalDateFormat={globalDateFormat ?? ''}
              setEpoch={setEpoch}
            />
          )}
          <Box className='Sod-ConsultAction-commentWrapper'>
            {isReviewMode && (
              <Typography kind='h3' color={CF_NEU_BLD}>
                {l('What are you working on during the review?')}
              </Typography>
            )}
            <Box
              tag='div'
              className={
                isReviewMode ? 'Sod-ConsultAction-commentBox--isReview' : ''
              }>
              <InputField
                name='move-to-open-action-comment'
                kind='multiline'
                label={
                  <Typography
                    kind='label'
                    htmlFor='move-to-open-action-comment'>
                    {l('Comments')}
                  </Typography>
                }
                tabIndex={0}
                value={commentValue}
                setValue={setCommentValue}
                placeholder={l(
                  isReviewMode
                    ? 'Describe the review activities'
                    : 'Provide a reason for your decision'
                )}
                isRequired={!isSubmitButtonEnable}
                isCritical={isCommentCritical}
                setIsCritical={setIsCommentCritical}
                CriticalHelperText={
                  <InlineMessage
                    text={
                      commentValue.trim().length < 1
                        ? l('This required field is incomplete')
                        : `Your comments must contain more than ${
                            MIN_COMMENT_LENGTH - 1
                          } characters.`
                    }
                    colorTheme='critical'
                    size='small'
                    leftIcon={<Icon kind='AlertCriticalFilled' />}
                  />
                }
              />
            </Box>
          </Box>
        </Box>
      }
      FooterActionBarComp={
        <FooterActionBar size='large' isScrolled={sectionRefIsScrolled}>
          <div />
          <div className='Sod-ConsultActionModal-footerActionButtons'>
            <Button
              type='button'
              kind='ghost'
              size='medium'
              isDisabled={isDisabled ?? isLoading}
              onClick={handleModalClose}>
              {l('Cancel')}
            </Button>
            <Button
              type='button'
              kind={isSubmitButtonEnable ? 'filled' : 'outlined'}
              size='medium'
              onClick={() => {
                if (isSubmitButtonEnable) {
                  handleModalSubmit();

                  return;
                }

                setIsCommentCritical(true);
              }}
              isDisabled={isDisabled && !isLoading}
              isLoading={isLoading}
              loaderText={l('Saving')}>
              {l('Save')}
            </Button>
          </div>
        </FooterActionBar>
      }
      className='Sod-ConsultActionModal'
    />
  );
}

SodConsultActionModal.defaultProps = {
  isDisabled: false,
  isReview: true,
  isEndDate: true,
  currentStartDate: undefined,
  currentEndDate: undefined,
  globalDateFormat: 'MMM dd, yyyy',
};

export default memo(SodConsultActionModal);
